import { List, AutoSizer } from 'react-virtualized';
import type { User } from '@/configs/apis/user';
import EmptyIcon from '@/assets/images/common/svg/empty.svg?react';
import type { Contact, CreateContactParams } from '@/contexts/contacts/types';
import type {
  ChatChatId,
  ChatParams,
  CreateChatParams,
} from '@/contexts/chat/scripts/hooks/useChats/types';
import MemberItem from '@/components/member-Item';
import { TagTypes } from '@/components/my-avatar/type';
import styles from './index.module.scss';
import OperationBar from './operation-bar';

export default function UserList({
  users,
  onCancel,
  addContacts,
  createChat,
  activeChat,
  contacts,
}: {
  users: User[];
  onCancel: () => void;
  addContacts: (contacts: CreateContactParams[]) => Promise<any>;
  createChat: (params: CreateChatParams) => Promise<void | ChatParams>;
  activeChat: (id: ChatChatId) => Promise<true | undefined>;
  contacts: Contact[];
}) {
  const onAdd = useCallback(
    async (user: User) => {
      const params: Contact = {
        id: 0,
        category: 0,
        user_id: user.userId,
        state_msg: '',
        name: user.name || '',
        username: user.username || '',
        avatar: user.avatar || '',
        remark_name: '',
        verified: 0,
        dnd: 0,
        org_id: 0,
        org_name: '',
        external: 0,
        role: 0,
        create_at: 0,
        update_at: 0,
      };
      const res = await createChat({ userId: params.user_id });
      if (res) {
        await addContacts([Object.assign(params, { chat_id: res.chat_id })]);
      }
    },
    [addContacts, createChat]
  );

  const rowRenderer = ({
    index,
    style,
  }: {
    index: number;
    style: React.CSSProperties;
  }) => {
    const user = users[index];
    return (
      <div data-index={index} style={style}>
        <MemberItem
          className={styles.item}
          showCheck={false}
          hoverable
          name={user.name}
          disabled={!(user.have_user_key && user.have_group_key)}
          avatarProps={{
            src: user.avatar!,
            size: 'medium',
            tagType: !(user.have_user_key && user.have_group_key)
              ? TagTypes.KEYERROR
              : undefined,
            info: user,
          }}
          shortName={user.username}
          key={user.userId}
          onClick={async () => {
            const res = await createChat({ userId: user.userId });
            if (res) {
              const isActivated = await activeChat(res.chat_id);
              if (isActivated) {
                onCancel();
              }
            }
          }}
          extra={
            <OperationBar
              disabled={!(user.have_user_key && user.have_group_key)}
              contacts={contacts}
              user={user}
              onAdd={onAdd}
            />
          }
        ></MemberItem>
      </div>
    );
  };

  return (
    <div className={styles.content}>
      <AutoSizer>
        {({ width, height }) => (
          <List
            className="scrollbar_hover"
            width={width}
            height={height}
            rowHeight={60}
            rowCount={users.length}
            rowRenderer={rowRenderer}
            noRowsRenderer={() => (
              <div className={styles.empty}>
                <div className={styles.emptyIcon}>
                  <EmptyIcon />
                </div>
                <div>There were no contacts, try a new search.</div>
              </div>
            )}
            scrollToIndex={0}
          />
        )}
      </AutoSizer>
    </div>
  );
}
